if (!customElements.get('product-form')) {
  // Machine-readable order type, written alongside the human-readable properties.
  // Underscore-prefixed so Shopify hides it from customer-facing cart and checkout
  // display while still recording it on the order.
  //
  // Everything that IDENTIFIES an order type keys off this. The visible properties
  // ("Order type" = "Pre-order") are translated copy: their names and values change
  // with the locale, so matching on them made cart isolation depend on translation
  // strings, and meant editing copy silently changed behaviour.
  const ORDER_TYPE_PROPERTY = '_order_type';

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.toggleDynamicCheckoutButton(this.submitButton.hasAttribute('disabled'));
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const formData = new FormData(this.form);

        // Claim the re-entry guard before the async compatibility check below.
        // submitToCart only sets it once its own fetch starts, so without this a
        // second click during the /cart.js round trip adds the item twice.
        this.submitButton.setAttribute('aria-disabled', true);

        // Pre-orders are reservations and must not share a cart with in-stock or
        // backorder items. Confirm the current cart is compatible before adding.
        this.ensureOrderTypeCompatible(formData).then((conflictMessage) => {
          if (conflictMessage) {
            this.submitButton.removeAttribute('aria-disabled');
            this.handleErrorMessage(conflictMessage);
            return;
          }
          this.submitToCart(evt, formData);
        });
      }

      submitToCart(evt, formData) {
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const cart = this.getCart();
        if (cart) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (this.shouldRetryPreorderOnCanonicalDomain(response, formData)) {
                this.submitPreorderOnCanonicalDomain(formData);
                return;
              }

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = text || window.variantStrings.addToCart;
        }

        this.toggleDynamicCheckoutButton(disable);
      }

      toggleDynamicCheckoutButton(disable = true) {
        this.querySelectorAll('.shopify-payment-button__button').forEach((button) => {
          if (disable) {
            button.setAttribute('disabled', 'disabled');
            button.setAttribute('aria-disabled', 'true');
            button.setAttribute('tabindex', '-1');
          } else {
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
            button.removeAttribute('tabindex');
          }
        });
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      shouldRetryPreorderOnCanonicalDomain(response, formData) {
        if (response.status !== 422) return false;
        if (!this.isPreorderFormData(formData)) return false;
        const message = `${response.message || ''} ${response.description || ''}`.toLowerCase();
        if (!message.includes('sold out')) return false;
        return Boolean(this.canonicalCartAddUrl) && this.canonicalCartAddUrl !== this.currentCartAddUrl;
      }

      submitPreorderOnCanonicalDomain(formData) {
        const fallbackForm = document.createElement('form');
        fallbackForm.method = 'post';
        fallbackForm.action = this.canonicalCartAddUrl;
        fallbackForm.hidden = true;

        formData.forEach((value, key) => {
          if (key === 'sections' || key === 'sections_url') return;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          fallbackForm.appendChild(input);
        });

        const returnTo = document.createElement('input');
        returnTo.type = 'hidden';
        returnTo.name = 'return_to';
        returnTo.value = window.routes?.cart_url || '/cart';
        fallbackForm.appendChild(returnTo);

        document.body.appendChild(fallbackForm);
        fallbackForm.submit();
      }

      // Resolves an order type from a line item's / form's properties.
      //
      // Prefers the machine-readable _order_type. Falls back to the old
      // human-readable property ONLY when matching both its key and its value, so
      // carts created before _order_type shipped keep working. The previous
      // implementation compared the value against EVERY visible property, so an
      // unrelated one — a gift-card message, a personalisation app's field —
      // holding the text "Pre-order" made an ordinary item behave as a reservation
      // and blocked further adds.
      //
      // The legacy branch can be deleted once open carts have turned over; the
      // theme has written _order_type since 2026-08-04.
      orderTypeFromProperties(properties) {
        if (!properties) return null;

        const machineValue = properties[ORDER_TYPE_PROPERTY];
        if (machineValue) return String(machineValue).trim().toLowerCase();

        const legacyKey = this.dataset.orderTypeKey;
        if (!legacyKey || properties[legacyKey] === undefined) return null;

        const legacyValue = String(properties[legacyKey]).trim().toLowerCase();
        const preorderValue = (this.dataset.preorderValue || 'pre-order').toLowerCase();
        if (legacyValue === preorderValue || legacyValue === 'preorder') return 'preorder';
        return null;
      }

      isPreorderFormData(formData) {
        const properties = {};
        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) properties[match[1]] = value;
        });
        return this.orderTypeFromProperties(properties) === 'preorder';
      }

      get canonicalCartAddUrl() {
        const canonicalCartAddUrl = this.dataset.canonicalCartAddUrl;
        if (!canonicalCartAddUrl) return null;

        try {
          return new URL(canonicalCartAddUrl, window.location.origin).href;
        } catch (error) {
          return null;
        }
      }

      get currentCartAddUrl() {
        try {
          return new URL(window.routes?.cart_add_url || '/cart/add', window.location.origin).href.replace(/\.js$/, '');
        } catch (error) {
          return null;
        }
      }

      getCart() {
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      // Resolves to a conflict message if adding this item would mix a pre-order
      // reservation with in-stock/backorder items (in either direction), else null.
      ensureOrderTypeCompatible(formData) {
        const addingPreorder = this.isPreorderFormData(formData);
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

        return fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cartState) => {
            const items = (cartState && cartState.items) || [];
            if (!items.length) return null;

            const hasPreorder = items.some((item) => this.cartItemIsPreorder(item));
            const hasOther = items.some((item) => !this.cartItemIsPreorder(item));

            // Quick-add forms sit in a narrow card column where the full sentences
            // wrap to roughly ten lines, tall enough to break the grid row's
            // alignment. Those forms set data-compact-errors and get a short variant.
            const strings = window.variantStrings || {};
            const compact = this.dataset.compactErrors === 'true';

            if (addingPreorder && hasOther) {
              return (
                (compact && strings.preorderMixErrorShort) ||
                strings.preorderMixError ||
                'Pre-order items must be ordered separately from other items.'
              );
            }
            if (!addingPreorder && hasPreorder) {
              return (
                (compact && strings.regularMixErrorShort) ||
                strings.regularMixError ||
                'Your cart has a pre-order reservation, which must be ordered separately.'
              );
            }
            return null;
          })
          .catch(() => null);
      }

      cartItemIsPreorder(item) {
        return this.orderTypeFromProperties(item && item.properties) === 'preorder';
      }
    }
  );
}
